import type { CountryCapability, DestinationOption } from "../api/catalog";

/** All destination currencies declared on a catalog country row. */
export const currenciesForCountry = (
  country: CountryCapability | undefined
): string[] => {
  if (!country) return [];
  const fromList = (country.currencyCodes ?? [])
    .map((c) => c.trim().toUpperCase())
    .filter(Boolean);
  if (fromList.length > 0) return [...new Set(fromList)];
  const single = (country.currencyCode ?? country.currency)
    ?.trim()
    .toUpperCase();
  return single ? [single] : [];
};

/**
 * ISO 3166-1 alpha-2 → local ISO 4217 currency. Only used to break ties when
 * a country offers several destination currencies.
 */
export const LOCAL_CURRENCY_BY_COUNTRY: Record<string, string> = {
  US: "USD",
  MX: "MXN",
  GT: "GTQ",
  AR: "ARS",
  CO: "COP",
  BR: "BRL",
  CL: "CLP",
  PE: "PEN",
  SV: "USD",
  HN: "HNL",
  NI: "NIO",
  CR: "CRC",
  PA: "USD",
  DO: "DOP",
  EC: "USD",
  PY: "PYG",
  BO: "BOB",
  VE: "VES",
  UY: "UYU",
  ES: "EUR",
  DE: "EUR",
  FR: "EUR",
};

const localCurrencyIfAllowed = (
  country: CountryCapability | undefined,
  codes: string[]
): string | undefined => {
  const local =
    LOCAL_CURRENCY_BY_COUNTRY[(country?.countryCode ?? "").toUpperCase()];
  return local && codes.includes(local) ? local : undefined;
};

/**
 * Pinned link currency when it's valid for the country, else the country's
 * local currency when the catalog allows it, else the first catalog code.
 */
export const pickDefaultDestinationCurrency = (
  country: CountryCapability | undefined,
  pinned?: string
): string | undefined => {
  const codes = currenciesForCountry(country);
  if (codes.length === 0) return undefined;
  const wanted = pinned?.trim().toUpperCase();
  if (wanted && codes.includes(wanted)) return wanted;
  return localCurrencyIfAllowed(country, codes) ?? codes[0];
};

/** Expand catalog countries into destination picker rows (país + moneda). */
export const expandCountryCurrencyOptions = (
  countries: CountryCapability[]
): DestinationOption[] => {
  const options: DestinationOption[] = [];
  for (const country of countries) {
    for (const currencyCode of currenciesForCountry(country)) {
      options.push({
        countryCode: country.countryCode,
        countryName: country.countryName,
        currencyCode,
        supportedByCorridors: country.supportedByCorridors,
        effectiveStatus: country.effectiveStatus,
      });
    }
  }
  return options;
};

/** Union `destinationCurrencyCodes` from session corridors, keyed by ISO2 dest. */
export const corridorCurrencyCodesByDestination = (
  corridors: Array<{
    destinationCountryCode?: string;
    destinationCurrencyCodes?: string[];
  }>
): Map<string, string[]> => {
  const map = new Map<string, string[]>();
  for (const corridor of corridors) {
    const dest = corridor.destinationCountryCode?.trim().toUpperCase();
    if (!dest) continue;
    const extra = (corridor.destinationCurrencyCodes ?? [])
      .map((c) => c.trim().toUpperCase())
      .filter(Boolean);
    if (extra.length === 0) continue;
    const existing = map.get(dest) ?? [];
    map.set(dest, [...new Set([...existing, ...extra])]);
  }
  return map;
};

/**
 * Catalog countries for the picker, unioned with corridor currency codes so a
 * portal USD pin is not dropped when `/catalog/countries` only lists ARS.
 */
export const mergeCatalogWithCorridorCurrencies = (
  countries: CountryCapability[],
  corridorDestinations: string[],
  corridorCodesByDest: Map<string, string[]>,
  displayName: (countryCode: string) => string
): CountryCapability[] => {
  const destSet = new Set(corridorDestinations.map((c) => c.toUpperCase()));
  const merged: CountryCapability[] = [];
  const present = new Set<string>();

  for (const country of countries) {
    const upper = country.countryCode.toUpperCase();
    if (destSet.size > 0 && !destSet.has(upper)) continue;
    const extra = corridorCodesByDest.get(upper) ?? [];
    const codes = [...new Set([...currenciesForCountry(country), ...extra])];
    merged.push(
      codes.length > 0 ? { ...country, currencyCodes: codes } : country
    );
    present.add(upper);
  }

  for (const dest of corridorDestinations) {
    const upper = dest.toUpperCase();
    if (present.has(upper)) continue;
    const extra = corridorCodesByDest.get(upper);
    if (!extra?.length) continue;
    merged.push({
      countryCode: upper,
      countryName: displayName(upper),
      currencyCodes: extra,
      supportedByCorridors: [],
      effectiveStatus: "AVAILABLE",
    });
  }

  return merged;
};
